/* eslint-disable react-hooks/exhaustive-deps */
import { useContext } from 'react';
import { Navigate, Route, RouterProvider, Routes, createHashRouter } from 'react-router-dom';
import { AuthContext } from '../context';
import { Constants } from '../utils';

// Layout
import Layout from './layout';

// Auth
import Login from '../screens/auth/login';
import Logout from '../screens/auth/logout';
import Recover from '../screens/auth/recover';
import Reset from '../screens/auth/reset';

// Admin Screens
import AdminHome from '../screens/client/home/home';
import Profile from '../screens/client/profile/profile';
import Vehicles from '../screens/client/vehicles/vehicles';
import VehiclesCreate from '../screens/client/vehicles/create-vehicle';
import Conversion from '../screens/client/conversion/conversion';
import RouteConfig from '../screens/client/route-config/route-config';
import Users from '../screens/client/users/users';
import MaintenancePlan from '../screens/client/maintenance-plan/maintenance-plan';
import MaintenancePlanEdit from '../screens/client/maintenance-plan/edit-maintenance-plan';
import BillingOwner from '../screens/client/billing/owner/billing';
import BillingDriver from '../screens/client/billing/driver/billing';
import BillingClient from '../screens/client/billing/client/billing';
import ReportBilling from '../screens/client/reports/billing';
import ReportBillingRoute from '../screens/client/reports/billing-route';
import ReportBillingRelation from '../screens/client/reports/billing-relation';
import ReportBillingProfit from '../screens/client/reports/billing-profit';

// Moderator Screens
import SupervisorHome from '../screens/moderator/home/supervisor';
import CoordinatorHome from '../screens/moderator/home/coordinator';
import ModeratorReportRoutesFinished from '../screens/moderator/reports/routes/finished/finished';
import ModeratorReportRoutesFinishedDuplicate from '../screens/moderator/reports/routes/finished/duplicate';
import ModeratorReportIncidents from '../screens/moderator/reports/incidents/incidents';
import ModeratorReportFailures from '../screens/moderator/reports/failures/failures';
import ModeratorReportVehicleFailures from '../screens/moderator/reports/failures/vehicle-failures';
import ModeratorReportDrivers from '../screens/moderator/reports/drivers/drivers';
import ModeratorMaintenance from '../screens/moderator/maintenance/maintenance';
import ModeratorMaintenanceReport from '../screens/moderator/maintenance/report';
import ModeratorPanel from '../screens/moderator/panel/panel';

// Owner Screens
import OwnerHome from '../screens/owner/home/home';
import OwnerReportIncidents from '../screens/owner/reports/incidents/incidents';
import OwnerReportFailures from '../screens/owner/reports/failures/failures';
import OwnerReportVehicleFailures from '../screens/owner/reports/failures/vehicle-failures';
import OwnerReportMaintenance from '../screens/owner/reports/maintenance/maintenance';
import OwnerReportCommissions from '../screens/owner/reports/commissions';

const Router = () => {
  const { isLogged, user, currentRouter } = useContext(AuthContext);

  const authRouter = createHashRouter([
    {
      index: true,
      path: '/',
      element: <Login />,
    },
    {
      path: '/recover',
      element: <Recover />,
    },
    {
      path: '/recover/:code',
      element: <Reset />,
    },
    {
      path: '*',
      element: <Navigate to="/" replace />,
    }
  ]);

  const adminRouter = createHashRouter([
    {
      path: '/logout',
      element: <Logout />,
    },
    {
      index: true,
      path: '*',
      Component: AdminRoutes,
    },
  ]);

  const moderatorRouter = createHashRouter([
    {
      path: '/logout',
      element: <Logout />,
    },
    {
      path: '/',
      Component: user?.moderator_type_id === Constants.MODERATOR_TYPE.SUPERVISOR ? SupervisorRoutes : CoordinatorRoutes,
    },
    {
      index: true,
      path: '*',
      Component: ModeratorRoutes,
    },
  ]);

  const ownerRouter = createHashRouter([
    {
      path: '/logout',
      element: <Logout />,
    },
    {
      index: true,
      path: '*',
      Component: OwnerRoutes,
    },
  ]);

  let router = authRouter;

  if (isLogged) {
    switch ((currentRouter || user?.level_id)) {
      case Constants.LEVELS.ADMIN:
        router = adminRouter;
        break;

      case Constants.LEVELS.MODERATOR:
        router = moderatorRouter;
        break;

      case Constants.LEVELS.OWNER:
        router = ownerRouter;
        break;

      default:
        router = authRouter;
        break;
    }
  }

  return (
    <RouterProvider router={router} />
  )
}

const AdminRoutes = () => (
  <Routes>
    <Route element={<Layout />}>
      <Route path="/" element={<AdminHome />} />
      <Route path="/perfil" element={<Profile />} />
      <Route path="/vehicles" element={<Vehicles />} />
      <Route path="/vehicles/create" element={<VehiclesCreate />} />
      <Route path="/vehicles/:id/edit" element={<VehiclesCreate />} />
      <Route path="/conversion" element={<Conversion />} />
      <Route path="/route-config" element={<RouteConfig />} />
      <Route path="/users" element={<Users />} />
      <Route path="/maintenance-plan" element={<MaintenancePlan />} />
      <Route path="/maintenance-plan/:id/edit" element={<MaintenancePlanEdit />} />
      <Route path="/billing/owner" element={<BillingOwner />} />
      <Route path="/billing/driver" element={<BillingDriver />} />
      <Route path="/billing/client" element={<BillingClient />} />
      <Route path="/report/billing" element={<ReportBilling />} />
      <Route path="/report/billing/route" element={<ReportBillingRoute />} />
      <Route path="/report/billing/relation" element={<ReportBillingRelation />} />
      <Route path="/report/billing/profit" element={<ReportBillingProfit />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
)

const SupervisorRoutes = ({ user }) => (
  <Routes>
    <Route element={<Layout />}>
      <Route path="/" element={<SupervisorHome />} />
    </Route>
  </Routes>
)

const CoordinatorRoutes = ({ user }) => (
  <Routes>
    <Route element={<Layout />}>
      <Route path="/" element={<CoordinatorHome />} />
    </Route>
  </Routes>
)

const ModeratorRoutes = ({ user }) => (
  <Routes>
    <Route element={<Layout />}>
      <Route path="/panel" element={<ModeratorPanel />} />
      <Route path="/report/finished" element={<ModeratorReportRoutesFinished />} />
      <Route path="/report/finished/:date/duplicate" element={<ModeratorReportRoutesFinishedDuplicate />} />
      <Route path="/report/incidents" element={<ModeratorReportIncidents />} />
      <Route path="/report/failures" element={<ModeratorReportFailures />} />
      <Route path="/report/failures/:plaque" element={<ModeratorReportVehicleFailures />} />
      <Route path="/report/drivers" element={<ModeratorReportDrivers />} />
      <Route path="/maintenance" element={<ModeratorMaintenance />} />
      <Route path="/maintenance/plan" element={<ModeratorMaintenance />} />
      <Route path="/maintenance/history" element={<ModeratorMaintenance />} />
      <Route path="/maintenance/report/:id" element={<ModeratorMaintenanceReport />} />
      <Route path="/maintenance-plan" element={<MaintenancePlan />} />
      <Route path="/maintenance-plan/:id/edit" element={<MaintenancePlanEdit />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
)

const OwnerRoutes = () => (
  <Routes>
    <Route element={<Layout />}>
      <Route path="/" element={<OwnerHome />} />
      <Route path="/report/incidents" element={<OwnerReportIncidents />} />
      <Route path="/report/failures" element={<OwnerReportFailures />} />
      <Route path="/report/failures/:plaque" element={<OwnerReportVehicleFailures />} />
      <Route path="/report/maintenance" element={<OwnerReportMaintenance />} />
      <Route path="/report/commissions" element={<OwnerReportCommissions />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
)

export default Router;